import { defineStore } from 'pinia';
import { ref } from 'vue';
import { useDutyApi } from '@/rennovation/common/services/api/useDutyApi';
import type { DeletedDutiesResponse, DeletedDuty } from '@/rennovation/planning-view/types/api/deleted-duty-response';

export const useDeletedDutyStore = defineStore('deletedDuty', () => {
	const dutyApi = useDutyApi();

	// =========================================
	// STATE
	// =========================================
	const duties = ref<DeletedDuty[]>([]);
	const currentChunk = ref(0);
	const allLoaded = ref(false);
	const loading = ref(false);
	const error = ref<string | null>(null);
	const fromDate = ref<string | null>(null);
	const toDate = ref<string | null>(null);

	// =========================================
	// ACTIONS
	// =========================================

	/**
	 * Updates filters and resets pagination automatically.
	 */
	function setFilters(from: string | null, to: string | null): void {
		fromDate.value = from;
		toDate.value = to;
		duties.value = [];
		currentChunk.value = 0;
		allLoaded.value = false;
	}

	async function loadChunk(): Promise<void> {
		if (loading.value || allLoaded.value) return;

		loading.value = true;
		error.value = null;

		try {
			const data: DeletedDutiesResponse = await dutyApi.loadDeletedDuties(currentChunk.value, fromDate.value, toDate.value);

			if (!data || data.length === 0) {
				allLoaded.value = true;
				return;
			}

			const existingDuties = new Map(duties.value.map((d) => [String(d.id), d]));
			data.forEach((newDuty) => {
				const dutyId: string = newDuty.id;
				existingDuties.set(dutyId, newDuty);
			});

			duties.value = Array.from(existingDuties.values());
			currentChunk.value++;
		} catch (err) {
			error.value = err instanceof Error ? err.message : 'Unknown error';
		} finally {
			loading.value = false;
		}
	}

	async function restore(dutyId: string): Promise<void> {
		loading.value = true;
		try {
			await dutyApi.restoreDuty(dutyId);
			duties.value = duties.value.filter((d) => String(d.id) !== String(dutyId));
		} catch (err) {
			error.value = err instanceof Error ? err.message : 'Unknown error';
			console.error('Failed to restore duty:', err);
		} finally {
			loading.value = false;
		}
	}

	function reset(): void {
		duties.value = [];
		currentChunk.value = 0;
		allLoaded.value = false;
		loading.value = false;
		error.value = null;
		fromDate.value = null;
		toDate.value = null;
	}

	return {
		duties,
		currentChunk,
		allLoaded,
		loading,
		error,
		fromDate,
		toDate,
		loadChunk,
		setFilters,
		restore,
		reset,
	};
});
